// src/components/FileUpload.jsx
import React, { useRef, useState } from 'react';
import { Upload, Loader2, AlertCircle, Play, CheckCircle, Info } from 'lucide-react';
import { Button } from './ui/button';

const FileUpload = ({
  onFileUpload,
  onComplete,
  loading,
  error,
  uploadedFile,
  mode = 'analysis' // 'analysis' or 'redesign'
}) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showAudienceForm, setShowAudienceForm] = useState(false);
  const [audienceData, setAudienceData] = useState({
    type: '',
    goal: '',
    size: '',
    context: '',
    retainerClient: ''
  });

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setShowAudienceForm(true);
    }
  };

  const handleAudienceDataChange = (field, value) => {
    setAudienceData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleStartAnalysis = () => {
    if (!selectedFile) return;
    // Support both onComplete (new) and onFileUpload (legacy)
    const callback = onComplete || onFileUpload;
    if (callback) {
      callback(selectedFile, audienceData);
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setShowAudienceForm(false);
    setAudienceData({ type: '', goal: '', size: '', context: '', retainerClient: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  const ErrorAlert = ({ message }) => (
    <div className="rounded-xl border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
      <div className="flex items-start gap-2">
        <AlertCircle className="mt-0.5 h-4 w-4" />
        <div>
          <p className="font-semibold">Upload error</p>
          <p className="mt-1 leading-relaxed text-destructive/90">{message}</p>
        </div>
      </div>
    </div>
  );

  const FilePreview = ({ file }) => (
    <div className="rounded-xl border border-accent/30 bg-accent/10 p-4 text-left">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/20">
          <CheckCircle className="h-5 w-5 text-accent" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">{file.name}</p>
          <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
        </div>
        <Button variant="ghost" size="sm" className="px-2 text-xs" onClick={resetUpload}>
          Change
        </Button>
      </div>
    </div>
  );

  const dropZoneClasses = selectedFile
    ? 'border-accent/60 bg-accent/5'
    : 'border-border bg-muted/10 dark:bg-muted/5';

  return (
    <div className="space-y-10">
      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="space-y-6">
          {error && <ErrorAlert message={error} />}

          <div
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            className={`group relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed ${dropZoneClasses} p-10 text-center transition hover:border-accent/80 hover:bg-accent/10`}
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/15 text-accent">
              <Upload className="h-6 w-6" />
            </div>
            <h3 className="mt-6 text-lg font-semibold text-foreground">Drop your presentation</h3>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              PPT, PPTX, PDF, or DOCX up to 200 MB. We keep it private and only use it for this analysis session.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                Browse files
              </Button>
              {selectedFile && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={(event) => {
                    event.stopPropagation();
                    resetUpload();
                  }}
                >
                  Clear selection
                </Button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".ppt,.pptx,.pdf,.doc,.docx"
              onChange={handleFileSelect}
              className="hidden"
            />
            {selectedFile && <div className="mt-6 w-full max-w-lg"><FilePreview file={selectedFile} /></div>}
          </div>

          {showAudienceForm && selectedFile && (
            <div className="rounded-2xl border border-border bg-card/80 p-6 shadow-sm">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Audience information <span className="text-xs font-normal text-muted-foreground">(optional)</span></h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Add contextual details so the recommendations feel tailored to your room.
                </p>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Audience type</label>
                  <select
                    value={audienceData.type}
                    onChange={(e) => handleAudienceDataChange('type', e.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none"
                  >
                    <option value="">Select audience</option>
                    <option value="executive">Executive/Leadership</option>
                    <option value="client">Client/Stakeholder</option>
                    <option value="team">Internal Team</option>
                    <option value="public">Public/Conference</option>
                    <option value="educational">Educational/Training</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Presentation goal</label>
                  <select
                    value={audienceData.goal}
                    onChange={(e) => handleAudienceDataChange('goal', e.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none"
                  >
                    <option value="">Select goal</option>
                    <option value="inform">Inform/Educate</option>
                    <option value="persuade">Persuade/Convince</option>
                    <option value="inspire">Inspire/Motivate</option>
                    <option value="align">Align/Coordinate</option>
                    <option value="sell">Sell/Pitch</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Audience size</label>
                  <select
                    value={audienceData.size}
                    onChange={(e) => handleAudienceDataChange('size', e.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none"
                  >
                    <option value="">Select size</option>
                    <option value="small">Small (1-10 people)</option>
                    <option value="medium">Medium (11-50 people)</option>
                    <option value="large">Large (50+ people)</option>
                    <option value="virtual">Virtual / Remote</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Retainer client</label>
                  <input
                    type="text"
                    value={audienceData.retainerClient}
                    onChange={(e) => handleAudienceDataChange('retainerClient', e.target.value)}
                    placeholder="Client name if applicable"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none"
                  />
                </div>
              </div>

              {/* Additional Options */}
              <div className="space-y-3 col-span-full">
                <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Additional Options</label>
                <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                  {/* Video Content Checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={audienceData.hasVideos || false}
                      onChange={(e) => handleAudienceDataChange('hasVideos', e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-border text-accent focus:ring-accent focus:ring-offset-0"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-foreground">
                        This presentation contains videos
                      </span>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Video files can cause rendering issues. We'll use placeholder thumbnails instead.
                      </p>
                    </div>
                  </label>

                  {/* Generate Edited Preview Checkbox - Only show for normal analysis, not redesign */}
                  {mode !== 'redesign' && (
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={audienceData.generateEditedPreview || false}
                        onChange={(e) => handleAudienceDataChange('generateEditedPreview', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-border text-accent focus:ring-accent focus:ring-offset-0"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-foreground">
                          Generate Edited Preview
                        </span>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Use Nano Banana Pro to generate an AI-enhanced preview of your slides incorporating the analysis.
                        </p>
                      </div>
                    </label>
                  )}
                </div>
              </div>

              <div className="mt-6 space-y-1">
                <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Additional context</label>
                <textarea
                  value={audienceData.context}
                  onChange={(e) => handleAudienceDataChange('context', e.target.value)}
                  placeholder="Anything else the AI should know before analysing your deck?"
                  rows={3}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none"
                />
              </div>

              <div className="mt-6 flex flex-col gap-3 md:flex-row">
                <Button
                  onClick={handleStartAnalysis}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Analysing�
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Play className="h-4 w-4" />
                      {mode === 'redesign' ? 'Start redesign' : 'Start analysis'}
                    </span>
                  )}
                </Button>
                <Button variant="outline" onClick={resetUpload} className="md:w-32">
                  Reset form
                </Button>
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-4 rounded-2xl border border-border bg-card/80 p-6 shadow-sm">
          <div className="inline-flex items-center gap-2 rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
            <Upload className="h-3.5 w-3.5" />
            AI upload studio
          </div>
          <h2 className="text-lg font-semibold text-foreground">Upload once, get insight-ready data.</h2>
          <p className="text-sm text-muted-foreground">
            We pull slide titles, speaker notes, fonts, colours, and layout cues to craft smarter prompts for the analysis engine.
          </p>
          <div className="space-y-3 rounded-xl border border-border/60 bg-background/60 p-4 text-sm text-muted-foreground">
            <p className="flex items-start gap-2">
              <span className="mt-1 block h-1.5 w-1.5 rounded-full bg-accent"></span>
              Parse every slide, note, and embedded text for AI context.
            </p>
            <p className="flex items-start gap-2">
              <span className="mt-1 block h-1.5 w-1.5 rounded-full bg-accent"></span>
              Blend optional audience context to tailor recommendations.
            </p>
            <p className="flex items-start gap-2">
              <span className="mt-1 block h-1.5 w-1.5 rounded-full bg-accent"></span>
              Produce actionable insights, exports, and chat-ready knowledge.
            </p>
          </div>
          <div className="flex items-start gap-2 rounded-xl border border-border/60 bg-background/60 p-4 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 text-accent" />
            <span>
              Need a demo? Drop any PDF or PPTX to see the analysis pipeline in action.
            </span>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default FileUpload;
